import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface FeaturesModalProps {
  open: boolean;
  onClose: () => void;
  features: Record<string, {
    enabled: boolean;
    firstUsedAt?: string;
    lastUsedAt?: string;
    lastDisabledAt?: string;
    usageCount?: number;
    [key: string]: any;
  }> | undefined;
  companyName: string;
}

/**
 * Modal showing all features adopted by an account
 */
export const FeaturesModal: React.FC<FeaturesModalProps> = ({
  open,
  onClose,
  features,
  companyName,
}) => {
  if (!features || Object.keys(features).length === 0) return null;

  // Convert to array of [name, feature] entries
  const featureEntries = Object.entries(features);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Features Adopted
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {companyName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total Features: <strong>{featureEntries.length}</strong>
          </Typography>

          <List>
            {featureEntries.map(([featureName, feature]) => (
              <ListItem
                key={featureName}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  '&:last-child': { mb: 0 },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {featureName}
                      </Typography>
                      {feature.enabled ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Enabled"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Disabled"
                          color="default"
                          size="small"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    feature.firstUsedAt && (
                      <Typography variant="caption" color="text.secondary">
                        First used: {new Date(feature.firstUsedAt).toLocaleDateString()}
                        {(feature.usageCount ?? 0) > 1 && ` • Used ${feature.usageCount} times`}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
